import path from "path";

import chalk from "chalk";

import type { BuildTask } from "../../../../Generator/src/generators/generate";
import { BuildClientJsTask } from "../../BuildTasks/ClientJS/BuildClientJsTask";
import { BuildLocalEngineTask } from "../../BuildTasks/Engine/BuildLocalEngineTask";
import {
    createClientViteConfigTask,
    createClientWebpackConfigTask,
    createEngineWebpackConfigTask,
} from "../../BuildTasks/EngineStructureUtils";
import { compileForm } from "../../BuildTasks/Form/CompileForm";
import { generateForm } from "../../BuildTasks/Form/GenerateForm";
import { writeFLangNormalizationFiles } from "../../BuildTasks/Form/WriteFLangNormalizationFiles";
import { FSTask } from "../../BuildTasks/FSTask";
import { generatorFactory, getGeneratorSourceKey } from "../../BuildTasks/Generator/AcquireFormGenerator";
import { resolveLocalEngineSource, resolveLocalGeneratorSource } from "../../BuildTasks/PublishedVersionUtils";
import { FormBuildMode, resolveFormInfo } from "../../BuildTasks/ResolveFormInfo";
import { writeFormMeta } from "../../BuildTasks/WriteFormMeta";
import { ConsoleApplicationContext } from "../../Commons/ConsoleApplication/ConsoleApplicationContext";
import { FormGlobs } from "../../Commons/FormGlobsUtils";
import { getLogger } from "../../Commons/Logging/StaticLogger";
import { Observe } from "../../Commons/TasksCore/Observe";
import { printTime } from "../../Commons/TimingUtils";
import { writeAttachmentPathFiles } from "../../BuildTasks/Form/WriteAttachmentPathFiles";
import { ChainableValueProducer } from "../../Commons/TasksCore/ChainableValueProducer";
import { EmptyProducer } from "../../Commons/TasksCore/EmptyProducer";
import { getRngSchemaFileExtension } from "../../../../Common/GetRngSchemaFileExtension";
import { extractGfvFromFormPath } from "../../../../Common/ExtractGFVPath/ExtractGFVPath";
import { WellKnownDirectories } from "../../../../Common/WellKnownDirectories";
import { writeAttachmentPathByGfvFiles } from "../../BuildTasks/Form/WriteAttachmentPathByGfvFiles";
import { writeKClangFiles } from "../../BuildTasks/Form/WriteKClangFiles";
import { writeExtendedSchemaFiles } from "../../BuildTasks/Form/WriteExtendedSchemaFiles";
import { BuildCandyWidgetTask } from "../../BuildTasks/CandyWidget/BuildCandyWidgetTask";

import { DevelopmentBuildOptions } from "./DevBuildOptions";

export async function runDevBuild(
    context: ConsoleApplicationContext,
    options: DevelopmentBuildOptions,
    engineDirectory: string,
    resourcesDirectory: string,
    farmDirectory: string,
    candyModulesDirectory: string
): Promise<void> {
    const devBuildOptions = options;

    const engineWebpackConfigTask = await createEngineWebpackConfigTask(engineDirectory);
    const buildEngineChain = options.skipBuildEngine
        ? new EmptyProducer()
        : engineWebpackConfigTask.chainWith(
              p => new BuildLocalEngineTask(p, resourcesDirectory, options.buildDebugOnly)
          );

    const clientWebpackConfigTask = await createClientWebpackConfigTask(engineDirectory);
    const buildClientJsChain = options.skipBuildEngine
        ? new EmptyProducer()
        : clientWebpackConfigTask.chainWith(p => new BuildClientJsTask(p, resourcesDirectory));

    const candyWidgetsConfigTask = await createClientViteConfigTask(engineDirectory);
    const buildCandyWidgetChain =
        options.skipBuildEngine || !options.buildCandyWidget
            ? new EmptyProducer()
            : candyWidgetsConfigTask.chainWith(p => new BuildCandyWidgetTask(p));

    const staticResourcesChain = Observe.files(
        path.join(__dirname, "..", "..", "StaticFilesForResourcesDir"),
        "*.js"
    ).do(x =>
        FSTask.copyRecursively(getLogger().withPrefix("STATICS"), x, path.join(resourcesDirectory, "public", "scripts"))
    );

    const pickListsChain = Observe.files(path.join(farmDirectory, "picklists"), "*.json").do(x =>
        FSTask.copyRecursively(context.logger.withPrefix("PICKLISTS"), x, path.join(resourcesDirectory, "picklists"))
    );

    const repositoryRoot = path.join(farmDirectory, "..");

    const formInfos = await FormGlobs.resolveGfvGlobs(options.formGlobs, farmDirectory);

    /** Конвертеры описи для форм, которые пока не реализованы на кенди, сама механика описи перенесена в формы в кенди апи, поэтому они должны быть в candy.resources
     *  Реализуется только при общем билде форм: candy build *
     * */
    let resourcesExternalConvertersChain: ChainableValueProducer<string> | EmptyProducer = Observe.empty();
    if (options.formGlobs[0] === "*") {
        resourcesExternalConvertersChain = Observe.files(path.join(farmDirectory, "externalConverters"), "*.xslt").do(
            x =>
                FSTask.copyRecursively(
                    getLogger().withPrefix("EXTERNAL-CONVERTERS"),
                    x,
                    path.join(resourcesDirectory, "converters")
                )
        );
    }

    const createChainForForm = async (formRelativePath: string) => {
        const gfv = extractGfvFromFormPath(formRelativePath);
        const formDirPath = path.join(repositoryRoot, formRelativePath);
        const formSchemasDirPath = path.join(formDirPath, "schemas");
        const rngSchemaMask = `*${getRngSchemaFileExtension(formSchemasDirPath, gfv)}`;

        return Observe.file(path.join(formDirPath, WellKnownDirectories.FormJsonFileName))
            .transform(formJsonFile => resolveFormInfo(formJsonFile, formDirPath))
            .transform(async x => ({
                ...x,
                generatorSource: resolveLocalGeneratorSource(engineDirectory),
                engineSource: resolveLocalEngineSource(engineDirectory),
            }))
            .attach(
                async fi => generatorFactory(fi.generatorSource, candyModulesDirectory, false, undefined),
                async (fi, y) => ({ ...fi, generator: y }),
                async fi => getGeneratorSourceKey(fi.generatorSource)
            )
            .attach(async fi =>
                devBuildOptions.watch
                    ? Observe.files(
                          fi.formSourcesPath,
                          "../../controls/**/*",
                          `./${WellKnownDirectories.FormJsonFileName}`,
                          "schemas/**/*",
                          "sugar/**/*",
                          "controls/**/*",
                          "autocalc/**/*",
                          "helpers/**/*",
                          "normalizers/**/*",
                          "validations/**/*",
                          "localization/**/*"
                      )
                    : Observe.value(fi.formSourcesPath)
            )
            .transform(async fi => ({
                ...fi,
                dependenciesHash:
                    fi.mode === FormBuildMode.NormalBuild
                        ? await fi.generator.getAllDependenciesHash(formDirPath, farmDirectory)
                        : "",
            }))
            .do(async fi => {
                if (options.skipGeneration || fi.mode !== FormBuildMode.NormalBuild) {
                    return undefined;
                }
                const task: BuildTask = {
                    gfv: fi.formName,
                    formPath: fi.formSourcesPath,
                    farmDirectory: farmDirectory,
                    resourcesHash: fi.dependenciesHash,
                };
                return generateForm(task, fi.generator, undefined);
            })
            .attach(async () =>
                devBuildOptions.watch
                    ? Observe.files(path.join(formDirPath, "form"), "**/*")
                    : Observe.value(path.join(formDirPath, "form"))
            )
            .do(async fi => {
                if (options.skipWebPackCompilation || fi.mode !== FormBuildMode.NormalBuild) {
                    return undefined;
                }
                return compileForm(
                    fi.formName,
                    formDirPath,
                    resourcesDirectory,
                    fi.dependenciesHash,
                    options.buildDebugOnly,
                    undefined,
                    false
                );
            })
            .do(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return;
                }
                await writeFLangNormalizationFiles(fi.formName, formDirPath, resourcesDirectory);
            })
            .do(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return;
                }
                await writeKClangFiles(fi.formName, formDirPath, resourcesDirectory);
            })
            .do(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return;
                }
                await writeExtendedSchemaFiles(fi.formName, formDirPath, resourcesDirectory);
            })
            .do(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return;
                }
                await writeAttachmentPathFiles(fi.formName, formDirPath, resourcesDirectory);
            })
            .do(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return;
                }
                await writeAttachmentPathByGfvFiles(fi.formName, formDirPath, resourcesDirectory);
            })
            .do(async fi => {
                if (fi.mode !== FormBuildMode.NormalBuild) {
                    return;
                }
                await writeFormMeta(
                    resourcesDirectory,
                    gfv,
                    fi.engineSource,
                    fi.dependenciesHash,
                    fi.themes,
                    fi.useServerSideNormalization
                );
            })
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "schemas"), rngSchemaMask).do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("SCHEMAS"),
                        x,
                        path.join(resourcesDirectory, "schemas")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "converters"), "*").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("CONVERTERS"),
                        x,
                        path.join(resourcesDirectory, "converters")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "mergers"), "*").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("MERGERS"),
                        x,
                        path.join(resourcesDirectory, "mergers")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "normalize"), "*.xml").do(x =>
                    FSTask.copyRecursively(
                        getLogger().withPrefix("NORMALIZE"),
                        x,
                        path.join(resourcesDirectory, "normalize")
                    )
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "patch"), "*.xslt").do(x =>
                    FSTask.copyRecursively(getLogger().withPrefix("PATCH"), x, path.join(resourcesDirectory, "patch"))
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "map"), "*.xml").do(x =>
                    FSTask.copyRecursively(getLogger().withPrefix("MAP"), x, path.join(resourcesDirectory, "map"))
                )
            )
            .attach(async fi =>
                Observe.files(path.join(fi.formSourcesPath, "unmap"), "*.xml").do(x =>
                    FSTask.copyRecursively(getLogger().withPrefix("UNMAP"), x, path.join(resourcesDirectory, "unmap"))
                )
            );
    };

    const buildFormsChain = Observe.fixedFormDirectories(
        formInfos.map(x => path.relative(repositoryRoot, x.formPath))
    ).splitToParallelChains(
        x => x,
        x => x,
        createChainForForm
    );

    if (devBuildOptions.watch) {
        const stopObserve1 = await buildEngineChain.observe();
        const stopObserve2 = await buildClientJsChain.observe();
        const stopObserve3 = await staticResourcesChain.observe();
        const stopObserve4 = await buildFormsChain.observe();
        const stopObserve5 = await pickListsChain.observe();
        const stopObserve6 = await resourcesExternalConvertersChain.observe();

        await context.waitForExit();

        await stopObserve1();
        await stopObserve2();
        await stopObserve3();
        await stopObserve4();
        await stopObserve5();
        await stopObserve6();
    } else {
        const startDate = Date.now();
        await Promise.all([
            buildEngineChain.currentValue,
            buildClientJsChain.currentValue,
            buildCandyWidgetChain.currentValue,
            staticResourcesChain.currentValue,
            resourcesExternalConvertersChain.currentValue,
            (async () => {
                try {
                    await pickListsChain.currentValue;
                    await buildFormsChain.currentValue;
                } catch (e) {
                    if (options.continueOnError) {
                        context.logger.error(e);
                    } else {
                        throw e;
                    }
                }
            })(),
        ]);
        const totalTime = Date.now() - startDate;
        // eslint-disable-next-line no-console
        console.log(`${chalk.greenBright("Build success")} Total time: ${printTime(totalTime)}`);
    }
}
